import type React from "react";
import { motion } from "framer-motion";
import { WalletTransaction, WalletTransactionType, WalletTransactionStatus } from "../../../types/stores";
import { TFunction } from "i18next";
import StatsIcons from "./StatsIcons";
import { StatCard } from "./StatCard";
import { TransactionRow } from "./TransactionRow";


interface OverviewTabProps {
  t: TFunction;
  stats: {
    todayRevenue: number;
    weeklyRevenue: number;
    monthlyRevenue: number;
    totalRevenue: number;
    successRate: number;
    successfulTransactions: number;
    failedTransactions: number;
    pendingTransactions: number;
    averageTransaction: number;
    highestTransaction: number;
    totalWithdrawals: number;
    pendingBalance: number;
    transactionByType: Record<WalletTransactionType, number>;
  };
  displayBalance: number;
  totalTransactions: number;
  recentTransactions: WalletTransaction[];
  getTransactionTypeText: (type: WalletTransactionType) => string;
  getStatusColor: (status: WalletTransactionStatus) => string;
}

export const renderOverviewTab = ({
  t,
  stats,
  displayBalance,
  totalTransactions,
  recentTransactions,
  getTransactionTypeText,
  getStatusColor,
}: OverviewTabProps) => {
  return (
    <div className="space-y-8">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={<StatsIcons.wallet />}
          title={t('wallet.balance', 'Current Balance')}
          value={`$${displayBalance?.toFixed(2) || '0.00'}`}
          subtitle={t('wallet.available', 'Available for withdrawal')}
          color="blue"
          trend={12.5}
        />
        
        <StatCard
          icon={<StatsIcons.revenue />}
          title={t('wallet.todayRevenue', "Today's Revenue")}
          value={`$${stats.todayRevenue.toFixed(2)}`}
          subtitle={`${totalTransactions} transactions`}
          color="green"
          trend={8.2}
        />
        
        <StatCard
          icon={<StatsIcons.trend />}
          title={t('wallet.weeklyRevenue', 'Weekly Revenue')}
          value={`$${stats.weeklyRevenue.toFixed(2)}`}
          subtitle="Last 7 days"
          color="purple"
          trend={15.3}
        />
        
        <StatCard
          icon={<StatsIcons.success />}
          title={t('wallet.successRate', 'Success Rate')}
          value={`${stats.successRate.toFixed(1)}%`}
          subtitle={`${stats.successfulTransactions} successful`}
          color="emerald"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Breakdown */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <StatsIcons.chart />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('wallet.revenueBreakdown', 'Revenue Breakdown')}
              </h3>
            </div>
            <span className="text-sm text-gray-500">Last 30 days</span>
          </div>
          
          <div className="space-y-4">
            {[
              { label: 'Today', value: stats.todayRevenue, color: 'bg-green-500', percentage: Math.min((stats.todayRevenue / 5000) * 100, 100) },
              { label: 'This Week', value: stats.weeklyRevenue, color: 'bg-blue-500', percentage: Math.min((stats.weeklyRevenue / 20000) * 100, 100) },
              { label: 'This Month', value: stats.monthlyRevenue, color: 'bg-purple-500', percentage: Math.min((stats.monthlyRevenue / 80000) * 100, 100) }
            ].map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600">{item.label}</span>
                  <span className="text-sm font-medium text-gray-900">
                    ${item.value.toFixed(2)}
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${item.percentage}%` }}
                    transition={{ duration: 1, delay: index * 0.1 }}
                    className={`h-full rounded-full ${item.color} transition-all duration-300`}
                  />
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Transaction Distribution */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <StatsIcons.transactions />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('wallet.transactionDistribution', 'Transaction Distribution')}
              </h3>
            </div>
          </div>
          
          <div className="space-y-3">
            {Object.entries(stats.transactionByType).map(([type, count]) => {
              const percentage = totalTransactions > 0 ? (count / totalTransactions) * 100 : 0;
              return (
                <div key={type} className="flex items-center">
                  <div className="w-32">
                    <span className="text-sm text-gray-600">
                      {getTransactionTypeText(type as WalletTransactionType)}
                    </span>
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        transition={{ duration: 1 }}
                        className="h-full bg-indigo-500 rounded-full"
                      />
                    </div>
                  </div>
                  <div className="w-16 text-right">
                    <span className="text-sm font-medium text-gray-900">
                      {count}
                    </span>
                    <span className="text-xs text-gray-500 ml-1">
                      ({percentage.toFixed(0)}%)
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Additional Stats & Recent Transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {t('wallet.recentTransactions', 'Recent Transactions')}
            </h3>
            <span className="text-sm text-gray-500">
              Last 10 transactions
            </span>
          </div>
          
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-3 px-4 text-left text-sm font-medium text-gray-500">Date</th>
                  <th className="py-3 px-4 text-left text-sm font-medium text-gray-500">Type</th>
                  <th className="py-3 px-4 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="py-3 px-4 text-left text-sm font-medium text-gray-500">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {recentTransactions.slice(0, 10).map((tx) => (
                  <TransactionRow
                    key={tx.id}
                    transaction={tx}
                    getTransactionTypeText={getTransactionTypeText}
                    getStatusColor={getStatusColor}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">
            {t('wallet.performance', 'Performance Summary')}
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Transactions</span>
              <span className="font-medium">{totalTransactions}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Average Transaction</span>
              <span className="font-medium text-green-600">${stats.averageTransaction.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Highest Transaction</span>
              <span className="font-medium text-purple-600">${stats.highestTransaction.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Successful</span>
              <span className="font-medium text-emerald-600">{stats.successfulTransactions}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Pending</span>
              <span className="font-medium text-amber-600">{stats.pendingTransactions}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Failed</span>
              <span className="font-medium text-rose-600">{stats.failedTransactions}</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};